import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  createFilterOptions,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AppButton,
  FilterBar,
  FilterDrawer,
} from "../../../../../components/ui";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import {
  buildActiveChips,
  clearFilterField,
  countActiveFilters,
  hasActiveFilters,
  resetFilters,
} from "./filtering";
import {
  AGE_OPTIONS,
  FILTER_FIELD_LABELS,
  MULTI_ROLE_OPTIONS,
  PENDING_OPTIONS,
  YES_NO_OPTIONS,
  type UsersFiltersState,
} from "./types";

type RoleOption = {
  value: string;
  label: string;
};

type UsersTableFiltersProps = {
  filters: UsersFiltersState;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onApply: (next: UsersFiltersState) => void;
  onClear: () => void;
  roleOptions: RoleOption[];
  emailDomainOptions: string[];
};

const ALL_OPTION = "All";

const stringFilterOptions = createFilterOptions<string>({
  matchFrom: "any",
  stringify: (option) => option,
  trim: true,
  ignoreCase: true,
});

function DateRangeFields({
  label,
  from,
  to,
  onFromChange,
  onToChange,
}: {
  label: string;
  from: Dayjs | null;
  to: Dayjs | null;
  onFromChange: (value: Dayjs | null) => void;
  onToChange: (value: Dayjs | null) => void;
}) {
  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.secondary"
        sx={{ mb: 1 }}
      >
        {label}
      </Typography>
      <Stack direction="row" spacing={1.25}>
        <DatePicker
          label="From"
          value={from}
          onChange={onFromChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="To"
          value={to}
          onChange={onToChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Stack>
    </Box>
  );
}

function ValueAutocomplete({
  label,
  value,
  options,
  onChange,
  placeholder = "Type to filter…",
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  return (
    <Autocomplete
      size="small"
      options={[{ value: "", label: ALL_OPTION }, ...options]}
      value={
        options.find((o) => o.value === value) ?? {
          value: "",
          label: ALL_OPTION,
        }
      }
      onChange={(_, next) => onChange(!next || !next.value ? "" : next.value)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      autoHighlight
      clearOnBlur={false}
      handleHomeEndKeys
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} />
      )}
      fullWidth
    />
  );
}

export function UsersTableFilters({
  filters,
  searchValue = "",
  onSearchChange,
  onApply,
  onClear,
  roleOptions,
  emailDomainOptions,
}: UsersTableFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<UsersFiltersState>(filters);
  const filtersActive = hasActiveFilters(filters);
  const draftActive = hasActiveFilters(draft);
  const activeCount = countActiveFilters(filters);
  const formatRole = (role: string) =>
    roleOptions.find((o) => o.value === role)?.label ?? role;
  const activeChips = buildActiveChips(filters, formatRole);

  useEffect(() => {
    if (drawerOpen) {
      setDraft(filters);
    }
  }, [drawerOpen, filters]);

  const patchDraft = (patch: Partial<UsersFiltersState>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const clearField = (key: keyof UsersFiltersState) => {
    onApply(clearFilterField(filters, key));
  };

  const handleApply = () => {
    onApply(draft);
    setDrawerOpen(false);
  };

  const clearDraft = () => setDraft(resetFilters());

  const handleClearAll = () => {
    onClear();
    onSearchChange?.("");
  };

  return (
    <>
      <FilterBar
        activeCount={activeCount}
        filtersActive={filtersActive}
        onOpenFilters={() => setDrawerOpen(true)}
        onClear={handleClearAll}
        clearDisabled={!filtersActive && !searchValue}
        chips={activeChips.map((chip) => ({
          key: String(chip.key),
          label: chip.label,
          onDelete: () => clearField(chip.key),
        }))}
        leading={
          <TextField
            size="small"
            placeholder="Search by name, email, phone, or role…"
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 280 },
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          />
        }
      />

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        subtitle="Apply to refresh the users table with these filters"
        activeCount={activeCount}
        actions={
          <>
            <AppButton
              variant="outlined"
              color="inherit"
              fullWidth
              startIcon={<FilterAltOffIcon />}
              onClick={clearDraft}
              disabled={!draftActive}
            >
              Clear filters
            </AppButton>
            <AppButton variant="contained" fullWidth onClick={handleApply}>
              Apply
            </AppButton>
          </>
        }
      >
        <Stack spacing={1.75}>
          <Autocomplete
            size="small"
            options={[ALL_OPTION, ...roleOptions.map((o) => o.label)]}
            value={
              draft.role
                ? roleOptions.find((o) => o.value === draft.role)?.label ??
                  ALL_OPTION
                : ALL_OPTION
            }
            onChange={(_, next) => {
              if (!next || next === ALL_OPTION) {
                patchDraft({ role: "" });
                return;
              }
              const match = roleOptions.find((o) => o.label === next);
              patchDraft({ role: match?.value ?? "" });
            }}
            filterOptions={stringFilterOptions}
            autoHighlight
            clearOnBlur={false}
            handleHomeEndKeys
            ListboxProps={{ style: { maxHeight: 280 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={FILTER_FIELD_LABELS.role}
                placeholder="Type to filter…"
              />
            )}
            fullWidth
          />

          <ValueAutocomplete
            label={FILTER_FIELD_LABELS.pending}
            value={draft.pending}
            options={[...PENDING_OPTIONS]}
            onChange={(pending) => patchDraft({ pending })}
          />

          <ValueAutocomplete
            label={FILTER_FIELD_LABELS.hasPhone}
            value={draft.hasPhone}
            options={[...YES_NO_OPTIONS]}
            onChange={(hasPhone) => patchDraft({ hasPhone })}
          />

          <Autocomplete
            size="small"
            options={[ALL_OPTION, ...emailDomainOptions]}
            value={draft.emailDomain || ALL_OPTION}
            onChange={(_, next) => {
              if (!next || next === ALL_OPTION) {
                patchDraft({ emailDomain: "" });
                return;
              }
              patchDraft({ emailDomain: next });
            }}
            filterOptions={stringFilterOptions}
            autoHighlight
            clearOnBlur={false}
            handleHomeEndKeys
            ListboxProps={{ style: { maxHeight: 280 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={FILTER_FIELD_LABELS.emailDomain}
                placeholder={
                  emailDomainOptions.length
                    ? "Type to filter…"
                    : "No domains found"
                }
              />
            )}
            fullWidth
          />

          <ValueAutocomplete
            label={FILTER_FIELD_LABELS.multiRole}
            value={draft.multiRole}
            options={[...MULTI_ROLE_OPTIONS]}
            onChange={(multiRole) => patchDraft({ multiRole })}
          />

          <ValueAutocomplete
            label={`${FILTER_FIELD_LABELS.joinedAge} (relative)`}
            value={draft.joinedAge}
            options={AGE_OPTIONS}
            onChange={(joinedAge) => patchDraft({ joinedAge })}
          />

          <ValueAutocomplete
            label={`${FILTER_FIELD_LABELS.updatedAge} (relative)`}
            value={draft.updatedAge}
            options={AGE_OPTIONS}
            onChange={(updatedAge) => patchDraft({ updatedAge })}
          />

          <DateRangeFields
            label="Joined (custom range)"
            from={draft.joinedFrom}
            to={draft.joinedTo}
            onFromChange={(value) => patchDraft({ joinedFrom: value })}
            onToChange={(value) => patchDraft({ joinedTo: value })}
          />

          <DateRangeFields
            label="Last updated (custom range)"
            from={draft.updatedFrom}
            to={draft.updatedTo}
            onFromChange={(value) => patchDraft({ updatedFrom: value })}
            onToChange={(value) => patchDraft({ updatedTo: value })}
          />
        </Stack>
      </FilterDrawer>
    </>
  );
}
